import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@material-ui/core';
import { toLocalDateInputValue } from './commercialDateTime';

const formatMinor = (minor, currency = 'EUR') => (Number(minor || 0) / 100).toLocaleString('en-IE', {
  style: 'currency', currency: currency || 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export default function CommercialPaymentScheduleDialog({
  agreement, installment = null, maximumMinor, businessDate, headers, hostname, onClose, onSaved,
}) {
  const editMode = Boolean(installment);
  const [form, setForm] = useState({
    dueDate: installment?.dueDate || businessDate || toLocalDateInputValue(),
    amount: Number(editMode ? installment.amountMinor : maximumMinor || 0) / 100,
    notes: installment?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const amountText = String(form.amount == null ? '' : form.amount).trim();
  const amountValue = Number(amountText);
  const validMinorPrecision = /^(?:\d+|\d*\.\d{1,2})$/.test(amountText);
  const amountMinor = validMinorPrecision && Number.isFinite(amountValue) ? Math.round(amountValue * 100) : 0;
  const amountError = !Number.isFinite(amountValue) || amountValue <= 0
    ? 'Stage amount must be greater than zero.'
    : !validMinorPrecision
      ? 'Use no more than two decimal places.'
    : amountMinor > Number(maximumMinor || 0)
      ? `Stage amount cannot exceed ${formatMinor(maximumMinor, agreement.currency)}.`
      : null;
  const valid = Boolean(form.dueDate) && !amountError;

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const path = `/commercial/commitments/${agreement.id}/installments${editMode ? `/${installment.id}` : ''}`;
      const response = await fetch(`${hostname}${path}`, {
        method: editMode ? 'PATCH' : 'POST',
        headers,
        body: JSON.stringify({ dueDate: form.dueDate, amount: amountValue, notes: form.notes.trim() }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || `Payment stage could not be ${editMode ? 'updated' : 'added'} (${response.status})`);
      setSaving(false);
      onSaved(result);
    } catch (cause) { setError(cause.message); setSaving(false); }
  };

  return <Dialog open fullWidth maxWidth="sm" onClose={() => !saving && onClose()} aria-labelledby="payment-stage-dialog-title">
    <DialogTitle id="payment-stage-dialog-title">{editMode ? 'Edit payment stage' : 'Add payment stage'} · {agreement.customerName}</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="textSecondary">
        Schedule when this agreement is expected to be collected. This changes planning only; it does not record cash or alter MRR.
      </Typography>
      <Box display="grid" gridGap={16} pt={2}>
        <TextField
          required type="date" variant="outlined" label="Stage due date"
          InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': 'Stage due date' }}
          value={form.dueDate} onChange={event => setForm(current => ({ ...current, dueDate: event.target.value }))}
        />
        <TextField
          required type="number" variant="outlined" label="Stage amount"
          inputProps={{ 'aria-label': 'Stage amount', min: .01, max: Number(maximumMinor || 0) / 100, step: .01 }}
          value={form.amount} error={Boolean(amountError)}
          helperText={amountError || `Maximum available for this stage: ${formatMinor(maximumMinor, agreement.currency)}`}
          onChange={event => setForm(current => ({ ...current, amount: event.target.value }))}
        />
        <TextField
          multiline minRows={2} variant="outlined" label="Schedule notes"
          inputProps={{ 'aria-label': 'Schedule notes', maxLength: 2000 }}
          value={form.notes} onChange={event => setForm(current => ({ ...current, notes: event.target.value }))}
        />
        {error && <Typography role="alert" color="error">{error}</Typography>}
      </Box>
    </DialogContent>
    <DialogActions>
      <Button disabled={saving} onClick={onClose}>Cancel</Button>
      <Button disabled={saving || !valid} color="primary" variant="contained" onClick={save}>
        {saving ? 'Saving…' : editMode ? 'Save stage changes' : 'Add payment stage'}
      </Button>
    </DialogActions>
  </Dialog>;
}
